import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';

type ProductImage = { id: string; url: string; principal: boolean; orden: number; variante_id: string };
type Variant = { id: string; nombre: string; images?: ProductImage[] };

type TipoVideoKey = 'influencer' | 'influencer_nuevo' | 'cinematografico' | 'personalizado';

// Options restricted to dev_tester accounts only

export const DisenarVideo: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(() => idsParam.split(',').map(s => s.trim()).filter(Boolean), [idsParam]);

  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, { full: string; thumbnail: string }>>({});
  const [isDevTester, setIsDevTester] = useState<boolean>(false);

  const { sidebarWidth, setMobileTitleOverride } = useSidebar();

  // Set mobile header title via sidebar context
  useEffect(() => {
    setMobileTitleOverride('Diseñá tu video');
    return () => setMobileTitleOverride(null);
  }, [setMobileTitleOverride]);

  // Fetch variants
  useEffect(() => {
    let cancelled = false;
    const fetchVariants = async () => {
      if (ids.length === 0) {
        setLoading(false);
        return;
      }
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No user');

        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();

        if (!profile?.id_company) throw new Error('No company');
        const idCompany = profile.id_company as string;
        if (!cancelled) setCompanyId(idCompany);

        const { data: companyData } = await supabase
          .from('company_profiles')
          .select('dev_tester')
          .eq('id_company', idCompany)
          .single();
        if (!cancelled && companyData?.dev_tester) setIsDevTester(true);

        const { data: variantsData, error: variantsError } = await supabase
          .from('variantes')
          .select('id, nombre')
          .in('id', ids)
          .eq('id_company', idCompany);

        if (variantsError) throw variantsError;
        if (!variantsData || variantsData.length === 0) {
          if (!cancelled) setVariants([]);
          return;
        }

        const variantIds = variantsData.map(v => v.id);
        const { data: imagesData } = await supabase
          .from('productos_imagenes')
          .select('id, url, principal, orden, variante_id')
          .in('variante_id', variantIds)
          .eq('id_company', idCompany);

        const imagesByVariant: Record<string, ProductImage[]> = {};
        (imagesData || []).forEach((img: any) => {
          if (!imagesByVariant[img.variante_id]) imagesByVariant[img.variante_id] = [];
          imagesByVariant[img.variante_id].push(img as ProductImage);
        });

        const merged: Variant[] = variantsData.map(v => ({
          ...v,
          images: imagesByVariant[v.id] || [],
        }));

        if (!cancelled) setVariants(merged);
      } catch (e) {
        console.error('Error fetching variants:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchVariants();
    return () => { cancelled = true; };
  }, [ids]);


  // Sign URLs for images
  useEffect(() => {
    if (!companyId || variants.length === 0) return;
    let cancelled = false;

    const signUrls = async () => {
      const newSignedUrls: Record<string, { full: string; thumbnail: string }> = {};

      for (const v of variants) {
        const principalImg = v.images?.find(i => i.principal) || v.images?.[0];
        if (!principalImg) continue;

        const fullPath = `${companyId}/${v.id}/full/${principalImg.url}`;
        const optPath = `${companyId}/${v.id}/opt/${principalImg.url}`;

        try {
          const { data: fullData } = await supabase.storage.from('productos').createSignedUrl(fullPath, 3600);
          const { data: optData } = await supabase.storage.from('productos').createSignedUrl(optPath, 3600);

          if (!cancelled) {
            newSignedUrls[v.id] = {
              full: fullData?.signedUrl || '',
              thumbnail: optData?.signedUrl || fullData?.signedUrl || '',
            };
          }
        } catch (e) {
          console.error('Error signing URL:', e);
        }
      }

      if (!cancelled) setSignedUrls(prev => ({ ...prev, ...newSignedUrls }));
    };

    signUrls();
    return () => { cancelled = true; };
  }, [companyId, variants]);

  const principalImageUrl = (v: Variant): string => {
    return signedUrls[v.id]?.thumbnail || '';
  };

  const tipoVideoOptions: Array<{ id: TipoVideoKey; name: string; description: string; materialIcon: string; badge?: string }> = [
    {
      id: 'influencer_nuevo',
      name: 'Influencer (nuevo)',
      description: 'Videos con diferentes estilos de grabación de influencer',
      materialIcon: 'videocam',
    },
    {
      id: 'influencer',
      name: 'Influencer',
      description: 'Video estilo contenido de redes sociales, dinámico y cercano',
      materialIcon: 'person',
    },
    {
      id: 'personalizado',
      name: 'Personalizado',
      description: 'Describí exactamente cómo querés que sea tu video',
      materialIcon: 'auto_fix_high',
    },
    {
      id: 'cinematografico',
      name: 'Cinematográfico',
      description: 'Video con estética profesional y tomas cinematográficas',
      materialIcon: 'movie',
      badge: 'Avanzado',
    },
  ];

  const tapFlash = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    el.classList.remove('tap-flash');
    void el.offsetWidth;
    el.classList.add('tap-flash');
  };

  const COMING_SOON_OPTIONS: TipoVideoKey[] = [];
  const DEV_ONLY_OPTIONS: TipoVideoKey[] = isDevTester ? [] : ['influencer'];

  const handleOptionClick = (optionId: TipoVideoKey) => {
    if (COMING_SOON_OPTIONS.includes(optionId)) return;
    if (DEV_ONLY_OPTIONS.includes(optionId)) return;
    if (optionId === 'influencer_nuevo') {
      navigate(`/redes-sociales/crear-anuncio/configurar-video-influencer-nuevo?ids=${ids.join(',')}`);
    } else if (optionId === 'influencer') {
      navigate(`/redes-sociales/crear-anuncio/configurar-video-influencer?ids=${ids.join(',')}`);
    } else if (optionId === 'personalizado') {
      navigate(`/redes-sociales/crear-anuncio/configurar-video-personalizado?ids=${ids.join(',')}`);
    } else if (optionId === 'cinematografico') {
      navigate(`/redes-sociales/crear-anuncio/configurar-video-cinematografico?ids=${ids.join(',')}`);
    }
  };

  return (
    <div className="min-h-[100dvh] md:min-h-screen bg-[#F2F2F7]">
      {/* Desktop sticky header — PruebaNuevaInterfaz style */}
      <div className="hidden sm:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="bg-white border-b border-black/[0.06]">
          <div className="max-w-4xl mx-auto px-8">
            <div className="flex items-center justify-between h-14">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Atrás</span>
              </button>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold text-brand-black/90">Diseñá tu video</h1>
              <div />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: constrained card layout */}
      <div className="hidden sm:block max-w-4xl mx-auto px-8 pt-[72px] pb-6">

        {/* Product selection — iOS grouped list */}
        <div className="mt-4">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
            {variants.length === 1 ? 'Producto seleccionado' : `${variants.length || 0} productos seleccionados`}
          </p>
          {loading ? (
            <div className="bg-white rounded-[13px] px-4 py-[11px]">
              <span className="text-[15px] text-brand-black/40">Cargando...</span>
            </div>
          ) : variants.length === 0 ? (
            <div className="bg-white rounded-[13px] px-4 py-[11px]">
              <span className="text-[15px] text-brand-black/40">No hay productos seleccionados</span>
            </div>
          ) : (
            <div className="bg-white rounded-[13px] overflow-hidden">
              {variants.map((v, i) => {
                const url = principalImageUrl(v);
                return (
                  <React.Fragment key={v.id}>
                    {i > 0 && <div className="ml-[60px] h-px bg-black/[0.08]" />}
                    <div className="flex items-center gap-3 px-4 py-[11px]">
                      <div className="shrink-0 w-10 h-10 rounded-[8px] overflow-hidden bg-black/[0.03]">
                        {url ? (
                          <img src={url} alt={v.nombre} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-brand-black/20">
                            <ImageIcon className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                      <span className="flex-1 min-w-0 text-[15px] text-brand-black/90 truncate">{v.nombre}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
          <div className="bg-white rounded-[13px] overflow-hidden mt-3">
            <button
              type="button"
              onClick={() => {
                const qp = new URLSearchParams();
                if (ids.length > 0) qp.set('ids', ids.join(','));
                const query = qp.toString();
                navigate(query ? `/redes-sociales/crear-anuncio/seleccionar-productos?${query}` : '/redes-sociales/crear-anuncio/seleccionar-productos');
              }}
              onClickCapture={tapFlash}
              className="w-full flex items-center gap-3 px-4 py-[11px] min-h-[54px] text-left transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[15px] text-[#1644EB]">Cambiar productos</p>
              </div>
              <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
            </button>
          </div>
        </div>

        {/* Tipo de video — iOS grouped list */}
        <div className="mt-6">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Tipo de video</p>
          <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
            {tipoVideoOptions.map((option, idx) => {
              const isComingSoon = COMING_SOON_OPTIONS.includes(option.id);
              const isDevOnly = DEV_ONLY_OPTIONS.includes(option.id);
              const isDisabled = isComingSoon || isDevOnly;
              if (isDevOnly) return null;
              return (
                <React.Fragment key={option.id}>
                  {idx > 0 && <div className="ml-[46px] h-px bg-black/[0.08]" />}
                  <button
                    type="button"
                    onClick={() => handleOptionClick(option.id)}
                    disabled={isDisabled}
                    onClickCapture={tapFlash}
                    className={`w-full px-4 py-[11px] text-[15px] transition-colors flex items-center justify-between ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`material-symbols-rounded text-[20px] shrink-0 ${!isDisabled ? 'text-[#1644EB]' : 'text-neutral-400'}`}>{option.materialIcon}</span>
                      <span className="text-left">
                        <span className="text-[15px] text-brand-black/90 flex items-center gap-2">
                          {option.name}
                          {option.badge && !isComingSoon && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-[6px] text-[11px] font-medium bg-[#1644EB]/10 text-[#1644EB]">
                              {option.badge}
                            </span>
                          )}
                          {isComingSoon && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-[6px] text-[11px] font-medium bg-black/[0.05] text-[#3C3C43]/40">
                              Próximamente
                            </span>
                          )}
                        </span>
                        <span className="block text-[13px] text-[#3C3C43]/60 mt-0.5">{option.description}</span>
                      </span>
                    </span>
                    {!isDisabled && <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

      </div>

      {/* Mobile layout */}
      <div className="sm:hidden px-4 pt-1 pb-28">

        {/* Product selection — iOS grouped list */}
        <div className="mt-2">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">
            {variants.length === 1 ? 'Producto seleccionado' : `${variants.length || 0} productos seleccionados`}
          </p>
          {loading ? (
            <div className="bg-white rounded-[13px] px-4 py-[11px]">
              <span className="text-[15px] text-brand-black/40">Cargando...</span>
            </div>
          ) : variants.length === 0 ? (
            <div className="bg-white rounded-[13px] px-4 py-[11px]">
              <span className="text-[15px] text-brand-black/40">No hay productos seleccionados</span>
            </div>
          ) : (
            <div className="bg-white rounded-[13px] overflow-hidden">
              {variants.map((v, i) => {
                const url = principalImageUrl(v);
                return (
                  <React.Fragment key={v.id}>
                    {i > 0 && <div className="ml-[60px] h-px bg-black/[0.08]" />}
                    <div className="flex items-center gap-3 px-4 py-[11px]">
                      <div className="shrink-0 w-10 h-10 rounded-[8px] overflow-hidden bg-black/[0.03]">
                        {url ? (
                          <img src={url} alt={v.nombre} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-brand-black/20">
                            <ImageIcon className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                      <span className="flex-1 min-w-0 text-[15px] text-brand-black/90 truncate">{v.nombre}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
          <div className="bg-white rounded-[13px] overflow-hidden mt-3">
            <button
              type="button"
              onClick={() => {
                const qp = new URLSearchParams();
                if (ids.length > 0) qp.set('ids', ids.join(','));
                const query = qp.toString();
                navigate(query ? `/redes-sociales/crear-anuncio/seleccionar-productos?${query}` : '/redes-sociales/crear-anuncio/seleccionar-productos');
              }}
              onClickCapture={tapFlash}
              className="w-full flex items-center gap-3 px-4 py-[11px] min-h-[54px] text-left transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[15px] text-[#1644EB]">Cambiar productos</p>
              </div>
              <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />
            </button>
          </div>
        </div>

        {/* Tipo de video — iOS grouped list */}
        <div className="mt-6">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Tipo de video</p>
          <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
            {tipoVideoOptions.map((option, idx) => {
              const isComingSoon = COMING_SOON_OPTIONS.includes(option.id);
              const isDevOnly = DEV_ONLY_OPTIONS.includes(option.id);
              const isDisabled = isComingSoon || isDevOnly;
              if (isDevOnly) return null;
              return (
                <React.Fragment key={option.id}>
                  {idx > 0 && <div className="ml-[46px] h-px bg-black/[0.08]" />}
                  <button
                    type="button"
                    onClick={() => handleOptionClick(option.id)}
                    disabled={isDisabled}
                    onClickCapture={tapFlash}
                    className={`w-full px-4 py-[11px] text-[15px] transition-colors flex items-center justify-between ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`material-symbols-rounded text-[20px] shrink-0 ${!isDisabled ? 'text-[#1644EB]' : 'text-neutral-400'}`}>{option.materialIcon}</span>
                      <span className="text-left">
                        <span className="text-[15px] text-brand-black/90 flex items-center gap-2">
                          {option.name}
                          {option.badge && !isComingSoon && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-[6px] text-[11px] font-medium bg-[#1644EB]/10 text-[#1644EB]">
                              {option.badge}
                            </span>
                          )}
                          {isComingSoon && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-[6px] text-[11px] font-medium bg-black/[0.05] text-[#3C3C43]/40">
                              Próximamente
                            </span>
                          )}
                        </span>
                        <span className="block text-[13px] text-[#3C3C43]/60 mt-0.5">{option.description}</span>
                      </span>
                    </span>
                    {!isDisabled && <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisenarVideo;
